import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

// Recycling, PFAS and REACH for a part, product or packaging item (the
// director, 15 Oct 2026; migration 073): "not shown outside Admin, just like
// invoices and price info". Directors only: for anyone else this renders
// nothing and asks for nothing. Loads and saves itself, beside the item.

const TEAL = '#14b8a6';
const BORD = '#2a3040';
const MUTED = '#6b7280';
const inp = { background: '#161b22', border: `1px solid ${BORD}`, borderRadius: '8px', padding: '7px 10px', fontSize: '13px', color: '#f0f2f5', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'system-ui' };

// The answers in one line, for a card: "PP 5 · recyclable · REACH certificate available".
export function complianceLine(entry) {
  if (!entry) return null;
  const said = [
    entry.recycle_code,
    entry.recyclable === 'yes' ? 'recyclable' : entry.recyclable === 'no' ? 'not recyclable' : null,
    entry.recycled_content === 'yes' ? 'recycled content' : null,
    entry.pfas === 'yes' && entry.reach === 'yes' ? 'PFAS + REACH ✓' : [entry.pfas === 'yes' && 'PFAS ✓', entry.reach === 'yes' && 'REACH ✓'].filter(Boolean).join(' · ') || null,
    entry.reach_certificate === 'available' ? 'certificate available' : entry.reach_certificate === 'requested' ? 'certificate requested' : null,
  ].filter(Boolean);
  return said.length ? said.join(' · ') : null;
}

// The form's fields, for a card that edits them itself or inside another form.
export function ComplianceFields({ fields, values, onChange }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px 12px' }}>
      {fields.map(fl => (
        <label key={fl.key} style={{ fontSize: '11px', color: MUTED, gridColumn: fl.key === 'note' ? '1 / -1' : undefined }}>{fl.label}
          {fl.kind === 'select'
            ? <select style={{ ...inp, marginTop: '4px' }} value={values[fl.key] || ''} onChange={e => onChange(fl.key, e.target.value || null)}>
                <option value="">Not said</option>
                {fl.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            : <input style={{ ...inp, marginTop: '4px' }} value={values[fl.key] || ''} placeholder={fl.placeholder || ''} onChange={e => onChange(fl.key, e.target.value)} />}
        </label>
      ))}
    </div>
  );
}

export default function ComplianceCard({ kind, id, cardStyle, titleStyle }) {
  const { isDirector } = useAuth();
  const { toast } = useToast();
  const director = isDirector();
  const [data, setData] = useState(null);       // { fields, entry }
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!director || !id) return;
    api.get(`/api/v1/compliance/${kind}/${id}`).then(r => setData(r.data)).catch(() => setData(null));
  }, [director, kind, id]);

  if (!director) return null;

  async function save() {
    setSaving(true);
    try {
      const { data: r } = await api.put(`/api/v1/compliance/${kind}/${id}`, form);
      setData(d => ({ ...d, entry: r.entry }));
      setForm(null);
      toast.success(r.changed ? 'Saved' : 'Nothing changed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  const label = (fl, v) => (fl.kind === 'select' ? (fl.options.find(o => o.value === v) || {}).label : v);
  return (
    <div style={cardStyle}>
      <div style={{ ...titleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Recycling &amp; compliance <i className="fa-solid fa-lock" title="Directors only" style={{ fontSize: '9px', marginLeft: '4px', color: MUTED }} /></span>
        {data && !form && (
          <button onClick={() => setForm({ ...data.entry })}
            style={{ background: 'none', border: '1px solid rgba(20,184,166,0.6)', color: TEAL, borderRadius: '6px', padding: '2px 9px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
            Edit
          </button>
        )}
      </div>
      {!data && <div style={{ fontSize: '12px', color: MUTED }}>Loading…</div>}
      {data && !form && (
        data.fields.every(fl => !data.entry[fl.key])
          ? <div style={{ fontSize: '12px', color: MUTED }}>Nothing entered yet.</div>
          : data.fields.filter(fl => data.entry[fl.key]).map(fl => (
            <div key={fl.key} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '5px 0', fontSize: '13px', borderBottom: `1px solid ${BORD}` }}>
              <span style={{ color: MUTED }}>{fl.label}</span>
              <span style={{ color: '#f0f2f5', textAlign: 'right' }}>{label(fl, data.entry[fl.key])}</span>
            </div>
          ))
      )}
      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <ComplianceFields fields={data.fields} values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={() => setForm(null)} style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ background: TEAL, border: 'none', color: '#0f172a', borderRadius: '8px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
